import { CompanyOfSearchModal } from '@doda/common/src/models/MasterCacheModel';

/**
 * 路線データ
 * RouteData
 * @param id ID
 * @param name 路線名
 * @param isChecked チェックされているかどうかのフラグ
 * @param isDisplay 表示されているかどうかのフラグ
 * @param stationList 駅の配列
 */
export interface RouteData {
  id: string;
  name: string;
  isChecked: boolean;
  isDisplay: boolean;
  stationList: StationData[];
}

/**
 * 駅データ
 * StationData
 * @param id ID
 * @param name 駅名
 * @param isChecked チェックされているかどうかのフラグ
 * @param isDisplay 表示されているかどうかのフラグ
 * @param routeId 路線のID
 */
export interface StationData {
  id: string;
  name: string;
  isChecked: boolean;
  isDisplay: boolean;
  routeId: string;
}

/**
 * 路線の配列生成関数
 * @param railwayCompany 路線マスタ
 * @param routeIdList 路線IDの配列
 * @returns 路線の配列
 */
export const createRouteData = (
  railwayCompany: CompanyOfSearchModal[],
  routeIdList: string[]
): Omit<RouteData, 'stationList'>[] => {
  return railwayCompany.flatMap(({ routeList }) =>
    routeList.map(({ id, name }) => {
      const routeChecked = isInList(routeIdList, id);
      return {
        id,
        name,
        isChecked: routeChecked,
        isDisplay: routeChecked,
      };
    })
  );
};

const isInList = (list: string[], id: string) => {
  return list.some((listId) => listId === id);
};

/**
 * 駅の配列生成関数
 * @param railwayCompany 路線マスタ
 * @param stationIdList 駅のIDの配列
 * @returns 駅の配列
 */
export const createStationData = (
  railwayCompany: CompanyOfSearchModal[],
  stationIdList: string[]
): StationData[] => {
  // isDisplayがtrueの要素のみ返却する
  return railwayCompany
    .flatMap((company) =>
      company.routeList.flatMap(({ stationList, id: routeId }) =>
        stationList.map(({ id, name }) => ({
          id,
          name,
          routeId,
          isChecked: stationIdList.some((query) => query === id),
          isDisplay: stationIdList.some((query) => query === id),
        }))
      )
    )
    .filter(({ isDisplay }) => isDisplay)
    .filter(
      (element, index, self) =>
        self.findIndex((e) => e.id === element.id) === index
    );
};

/**
 * 表示用配列の生成処理
 * @param routeList 路線の配列
 * @param stationList 駅の配列
 * @returns 路線・駅の表示用配列
 */
export const createRouteStationDisplayList = (
  routeList: Omit<RouteData, 'stationList'>[],
  stationList: StationData[]
): RouteData[] => {
  // isDisplayがtrueもしくはisDisplayがtrueの子要素を持つ要素に絞る
  const routeData = routeList.filter(
    ({ isDisplay, id }) =>
      isDisplay || stationList.some(({ routeId }) => id === routeId)
  );
  return routeData.map((route) => ({
    ...route,
    stationList: stationList.filter(({ routeId }) => routeId === route.id),
  }));
};
